/** @file What deleting entities does: the delete commands and the count of everything else they remove. */
import { collectRemovals } from '@pose2d/format/doc/collectRemovals';
import { ENTITY_KINDS } from '@pose2d/format/doc/entityKinds';
import type { CommandCall, Doc, EntityKind, Id } from '@pose2d/format/types';
import { deletable } from './deletable';

/** One entity to delete. */
export interface DeleteTarget {
  kind: EntityKind;
  id: Id;
}

/** The delete calls, the targets they are for (refused ones are left out), and, per kind in ENTITY_KINDS order, how many other entities go with them. */
export interface DeletePlan {
  calls: CommandCall[];
  planned: DeleteTarget[];
  alsoRemoved: { kind: EntityKind; count: number }[];
}

const COMMAND: Readonly<Record<EntityKind, string>> = {
  image: 'image.delete', bone: 'bone.delete', slot: 'slot.delete', attachment: 'attachment.delete', skin: 'skin.delete',
  ik: 'ik.delete', event: 'event.delete', socket: 'socket.delete', animation: 'animation.delete', reference: 'reference.remove',
};

/** Targets in order, minus those the commands would refuse (see `deletable`) and those another target's cascade already removes. */
export function deletePlan(doc: Doc, targets: readonly DeleteTarget[]): DeletePlan {
  const removed = new Map<Id, EntityKind>();
  const calls: CommandCall[] = [];
  const planned = targets.filter((target) => deletable(doc, target));
  for (const target of planned) {
    if (removed.has(target.id)) continue;
    for (const [id, kind] of collectRemovals(doc, target.kind, target.id)) removed.set(id, kind);
    calls.push({ name: COMMAND[target.kind], input: { target: target.id } });
  }
  const chosen = new Set(planned.map((t) => t.id));
  const counts = new Map<EntityKind, number>();
  for (const [id, kind] of removed) if (!chosen.has(id)) counts.set(kind, (counts.get(kind) ?? 0) + 1);
  const alsoRemoved = (Object.keys(ENTITY_KINDS) as EntityKind[]).flatMap((kind) => {
    const count = counts.get(kind) ?? 0;
    return count > 0 ? [{ kind, count }] : [];
  });
  return { calls, alsoRemoved, planned };
}
